import {Injectable, OnDestroy} from '@angular/core';
import {MsalBroadcastService, MsalService} from '@azure/msal-angular';
import {NbDialogService} from '@nebular/theme';
import {CookieService} from 'ngx-cookie-service';
import {NGXLogger} from 'ngx-logger';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {ConfirmPromptComponent} from '../../@theme/components/modal/confirm-prompt.component';
import {ApplicationInsightsService} from '../utils/application-insights.service';
import {User} from './user';
import {takeUntil} from 'rxjs/operators';
import {environment} from '../../../environments/environment';

// Based on code by alain-charles (https://github.com/alain-charles)
// From https://github.com/akveo/nebular/issues/706#issuecomment-421294590
@Injectable()
/**
 * Handles userservice for this module.
 */
export class UserService implements OnDestroy {

  protected user: BehaviorSubject<any | User> = new BehaviorSubject(this.getUser);
  private readonly destroy$ = new Subject<boolean>();
  private readonly cookieKey = `msal.${environment.azureClientId}.idtoken`;

  constructor(
    private logger: NGXLogger,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private cookieService: CookieService,
    private dialogService: NbDialogService,
    private applicationInsightService: ApplicationInsightsService) {
    this.onUserChange()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (user) => {
          if (user == null) {
            this.applicationInsightService.clearUserId();
          } else {
            this.applicationInsightService.setUserId(user.preferred_username || user.name || user.oid);
          }
        }
      });
  }

  private get getUser(): any {
    const currentUser = this.authService.instance.getActiveAccount()?.idTokenClaims;
    if (currentUser != null) {
      return currentUser;
    } else {
      return null;
    }
  }

  /**
   * Handles publishuser for this module.
   */
  publishUser(user: User | any): void {
    this.user.next(user);
  }

  /**
   * Handles onuserchange for this module.
   */
  onUserChange(): Observable<any> {
    return this.user;
  }

  /**
   * Handles signoutuser for this module.
   */
  signOutUser() {
    this.dialogService.open(ConfirmPromptComponent, {
      hasBackdrop: true, closeOnEsc: true, autoFocus: true,
      context: {
        title: 'Are you sure you want to logout?',
        body: 'You will lose any data that has not been saved yet!',
        button3Text: 'Cancel',
        button3Class: 'btn btn-info float-end',
        button1Text: 'Logout',
      },
    }).onClose
      .pipe(takeUntil(this.destroy$))
      .subscribe({next: (confirm) => {
        if (confirm === true) {
          this.applicationInsightService.clearUserId();
          this.authService.logoutRedirect();
          sessionStorage.clear();
          this.cookieService.deleteAll('/', window.location.hostname);
        }
      }});
  }

  /**
   * Handles ngondestroy for this module.
   */
  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  // Useful for saving JWT idToken e.g. for Hangfire
  /**
   * Handles saveidtoken for this module.
   */
  saveIdToken(idToken: string, date: Date) {
    if (idToken == null) {
      this.cookieService.delete(this.cookieKey, '/');
    } else {
      this.cookieService.set(this.cookieKey, idToken, date, '/');
    }
  }

}


